import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { paymentService, groupService } from '../../services/api';

/**
 * Página de pagamento PIX.
 * Exibe o QR Code e código copia e cola para pagamento.
 */
const PaymentPage = () => {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [manualActionLoading, setManualActionLoading] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isPayer = payment && currentUser && payment.payerId === currentUser.userId;
  const isReceiver = payment && currentUser && payment.receiverId === currentUser.userId;

  // Função para compartilhar pagamento
  const handleShare = (privacyLevel = 'HYBRID') => {
    const shareUrl = `${window.location.origin}/payment/${paymentId}?privacy=${privacyLevel}`;
    const shareText = `Pagamento Dividu - ${formatCurrency(payment.amount)}\nGrupo: ${payment.groupName}`;
    
    if (navigator.share) {
      navigator.share({
        title: 'Pagamento Dividu',
        text: shareText,
        url: shareUrl
      }).catch(() => {
        copyShareLink(shareUrl);
      });
    } else {
      copyShareLink(shareUrl);
    }
    setShareMenuOpen(false);
  };

  const copyShareLink = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      alert('Link copiado! Compartilhe com seus amigos.');
    });
  };

  // Calcula o tempo restante até a expiração
  const calculateTimeRemaining = useCallback((expirationDate) => {
    if (!expirationDate) return null;
    
    const now = new Date().getTime();
    const expiration = new Date(expirationDate).getTime();
    const diff = expiration - now;
    
    if (diff <= 0) {
      return null;
    }
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    return { hours, minutes, seconds, totalSeconds: Math.floor(diff / 1000) };
  }, []);

  // Formata o tempo para exibição
  const formatTimeRemaining = (time) => {
    if (!time) return '00:00:00';
    const { hours, minutes, seconds } = time;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Efeito para a contagem regressiva
  useEffect(() => {
    if (!payment?.expirationDate || payment.paymentStatus !== 'PENDING') return;
    
    const updateTimer = () => {
      const remaining = calculateTimeRemaining(payment.expirationDate);
      
      if (!remaining) {
        setIsExpired(true);
        setTimeRemaining(null);
        // Atualiza o status do pagamento quando expira
        handleExpiration();
      } else {
        setTimeRemaining(remaining);
      }
    };
    
    // Atualiza imediatamente
    updateTimer();
    
    // Atualiza a cada segundo
    const timerInterval = setInterval(updateTimer, 1000);
    
    return () => clearInterval(timerInterval);
  }, [payment?.expirationDate, payment?.paymentStatus, calculateTimeRemaining]);

  // Função para tratar a expiração
  const handleExpiration = async () => {
    try {
      // Marca o pagamento como expirado no servidor
      const data = await paymentService.expire(paymentId);
      setPayment(data);
    } catch (err) {
      console.error('Erro ao expirar pagamento:', err);
      // Força o status de expirado localmente
      setPayment(prev => ({ ...prev, paymentStatus: 'EXPIRED' }));
    }
  };

  useEffect(() => {
    loadPayment();
    
    // Atualiza o status a cada 10 segundos
    const interval = setInterval(() => {
      if (payment?.paymentStatus === 'PENDING') {
        refreshPaymentStatus();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [paymentId]);

  const scheduleRedirectIfCompleted = (data) => {
    if (data.paymentStatus === 'CONFIRMED' && data.transferStatus === 'COMPLETED') {
      setTimeout(() => {
        navigate(`/groups/${data.groupId}`);
      }, 3000);
    }
  };

  const loadPayment = async () => {
    try {
      const data = await paymentService.getById(paymentId);
      setPayment(data);
      scheduleRedirectIfCompleted(data);
    } catch (err) {
      console.error(err);
      setError('Erro ao carregar pagamento');
    } finally {
      setLoading(false);
    }
  };

  const refreshPaymentStatus = async () => {
    if (refreshing) return;
    
    try {
      setRefreshing(true);
      const data = await paymentService.refreshStatus(paymentId);
      setPayment(data);
      scheduleRedirectIfCompleted(data);
    } catch (err) {
      console.error(err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleCopyPixCode = () => {
    if (payment?.pixCopyPaste) {
      navigator.clipboard.writeText(payment.pixCopyPaste);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleCancel = async () => {
    if (window.confirm('Tem certeza que deseja cancelar este pagamento?')) {
      try {
        await paymentService.cancel(paymentId);
        navigate(`/groups/${payment.groupId}`);
      } catch (err) {
        console.error(err);
        setError('Erro ao cancelar pagamento');
      }
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Carregando pagamento...</p>
        </div>
      </div>
    );
  }

  if (error || !payment) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <div className="empty-state-icon"><i className="fa-duotone fa-triangle-exclamation"></i></div>
          <h3 className="empty-state-title">{error || 'Pagamento não encontrado'}</h3>
          <button onClick={() => navigate(-1)} className="btn btn-primary">
            Voltar
          </button>
        </div>
      </div>
    );
  }

  // Pagamento confirmado
  if (payment.paymentStatus === 'CONFIRMED') {
    // Fluxo manual: mostrar status e botões se repasse automático está desativado
    const showManualFlow = payment.transferStatus === 'NOT_APPLICABLE' || payment.transferStatus === 'FAILED';
    const transferCompleted = payment.transferStatus === 'COMPLETED';

    return (
      <div className="page-container">
        <div className="card-modern animate-fadeIn" style={{ padding: 32, textAlign: 'center' }}>
          <div 
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--success-color), #34D399)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              fontSize: 40,
              color: 'white'
            }}
          >
            <i className="fa-solid fa-check"></i>
          </div>
          
          <h2 style={{ margin: '0 0 8px', color: 'var(--success-color)' }}>
            Pagamento Confirmado!
          </h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
            O pagamento de {formatCurrency(payment.amount)} foi realizado com sucesso.
          </p>

          {transferCompleted ? (
            <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>
              Redirecionando para o grupo...
            </p>
          ) : showManualFlow ? (
                <div className="alert alert-info" style={{ textAlign: 'left', marginBottom: 16 }}>
                  <strong>Controle manual:</strong> O repasse automático está desativado. O pagador deve marcar como pago e o recebedor confirmar o recebimento.
                  <div style={{ marginTop: 8 }}>
                    <ul style={{ textAlign: 'left', fontSize: 14, color: 'var(--text-secondary)' }}>
                      <li>Status: {payment.manualPaid ? 'Pago' : 'Aguardando pagamento'} / {payment.manualReceived ? 'Recebido' : 'Aguardando confirmação'}</li>
                      {payment.manualPaidAt && <li>Pago em: {formatDate(payment.manualPaidAt)}</li>}
                      {payment.manualReceivedAt && <li>Recebido em: {formatDate(payment.manualReceivedAt)}</li>}
                    </ul>
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {isPayer && !payment.manualPaid && (
                      <button
                        className="btn btn-primary"
                        disabled={manualActionLoading}
                        onClick={async () => {
                          setManualActionLoading(true);
                          setError(null);
                          try {
                            const data = await paymentService.markManualPaid(paymentId);
                            setPayment(data);
                          } catch (err) {
                            setError('Erro ao marcar como pago.');
                          } finally {
                            setManualActionLoading(false);
                          }
                        }}
                      >
                        <i className={`fa-solid ${manualActionLoading ? 'fa-rotate fa-spin' : 'fa-money-bill'}`}></i>
                        {manualActionLoading ? ' Marcando...' : 'Marcar como pago'}
                      </button>
                    )}
                    {isReceiver && payment.manualPaid && !payment.manualReceived && (
                      <button
                        className="btn btn-success"
                        disabled={manualActionLoading}
                        onClick={async () => {
                          setManualActionLoading(true);
                          setError(null);
                          try {
                            const data = await paymentService.markManualReceived(paymentId);
                            setPayment(data);
                          } catch (err) {
                            setError('Erro ao confirmar recebimento.');
                          } finally {
                            setManualActionLoading(false);
                          }
                        }}
                      >
                        <i className={`fa-solid ${manualActionLoading ? 'fa-rotate fa-spin' : 'fa-hand-holding-dollar'}`}></i>
                        {manualActionLoading ? ' Confirmando...' : 'Confirmar recebimento'}
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="alert alert-warning" style={{ textAlign: 'left', marginBottom: 16 }}>
                  <strong>Repasse pendente.</strong>{' '}
                  {payment.receiverHasPixKey === false
                    ? 'O recebedor ainda não cadastrou a chave PIX.'
                    : 'Tente reenviar o repasse agora.'}
                  {payment.transferError && (
                    <div style={{ marginTop: 6, color: 'var(--text-secondary)' }}>
                      Detalhe: {payment.transferError}
                    </div>
                  )}
                </div>
              )}

          {!transferCompleted && !showManualFlow && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
              {payment.receiverHasPixKey === false ? (
                <Link to="/settings/pix" className="btn btn-primary">
                  <i className="fa-solid fa-key"></i> Cadastrar chave PIX
                </Link>
              ) : (
                <button 
                  className="btn btn-primary"
                  onClick={async () => {
                    setRetrying(true);
                    setError(null);
                    try {
                      const data = await paymentService.retryTransfer(paymentId);
                      setPayment(data);
                      scheduleRedirectIfCompleted(data);
                    } catch (err) {
                      console.error(err);
                      setError('Erro ao reprocessar repasse. Tente novamente.');
                    } finally {
                      setRetrying(false);
                    }
                  }}
                  disabled={retrying}
                >
                  <i className={`fa-solid ${retrying ? 'fa-rotate fa-spin' : 'fa-paper-plane'}`}></i>
                  {retrying ? ' Reenviando...' : ' Repassar agora'}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Botão Flutuante de Compartilhar - Pagamento Confirmado */}
        <div style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1000
        }}>
          {shareMenuOpen && (
            <div style={{
              position: 'absolute',
              bottom: 70,
              right: 0,
              background: 'white',
              borderRadius: 12,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              padding: 12,
              minWidth: 200,
              animation: 'slideUp 0.3s ease'
            }}>
              <button
                onClick={() => handleShare('COMPLETE')}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '10px 12px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderRadius: 8,
                  fontSize: 14
                }}
                onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
                onMouseOut={(e) => e.target.style.background = 'transparent'}
              >
                <strong>Completo</strong>
                <div style={{ fontSize: 12, color: '#666' }}>Mostra todos os nomes</div>
              </button>
              <button
                onClick={() => handleShare('HYBRID')}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '10px 12px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderRadius: 8,
                  fontSize: 14
                }}
                onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
                onMouseOut={(e) => e.target.style.background = 'transparent'}
              >
                <strong>Parcial (Recomendado)</strong>
                <div style={{ fontSize: 12, color: '#666' }}>Mostra apenas iniciais</div>
              </button>
              <button
                onClick={() => handleShare('ANONYMOUS')}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '10px 12px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderRadius: 8,
                  fontSize: 14
                }}
                onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
                onMouseOut={(e) => e.target.style.background = 'transparent'}
              >
                <strong>Anônimo</strong>
                <div style={{ fontSize: 12, color: '#666' }}>Oculta nomes</div>
              </button>
            </div>
          )}
          <button
            onClick={() => setShareMenuOpen(!shareMenuOpen)}
            style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: 'var(--primary-gradient)',
              border: 'none',
              color: 'white',
              fontSize: 24,
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(37, 99, 235, 0.4)',
              transition: 'transform 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'scale(1.1)'}
            onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
          >
            <i className="fa-solid fa-share-nodes"></i>
          </button>
        </div>
      </div>
    );
  }

  // Pagamento expirado ou cancelado
  if (payment.paymentStatus === 'EXPIRED' || payment.paymentStatus === 'CANCELLED') {
    return (
      <div className="page-container">
        <div className="card-modern animate-fadeIn" style={{ padding: 32, textAlign: 'center' }}>
          <div 
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--error-color), #f45c43)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              fontSize: 40,
              color: 'white'
            }}
          >
            <i className="fa-solid fa-xmark"></i>
          </div>
          
          <h2 style={{ margin: '0 0 8px', color: 'var(--error-color)' }}>
            {payment.paymentStatus === 'EXPIRED' ? 'Pagamento Expirado' : 'Pagamento Cancelado'}
          </h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>
            {payment.paymentStatus === 'EXPIRED' 
              ? 'O tempo para pagamento expirou. Gere um novo PIX se necessário.'
              : 'Este pagamento foi cancelado.'}
          </p>
          
          <button 
            onClick={() => navigate(`/groups/${payment.groupId}`)}
            className="btn btn-primary"
          >
            Voltar ao Grupo
          </button>
        </div>

        {/* Botão Flutuante de Compartilhar - Pagamento Expirado/Cancelado */}
        <div style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1000
        }}>
          {shareMenuOpen && (
            <div style={{
              position: 'absolute',
              bottom: 70,
              right: 0,
              background: 'white',
              borderRadius: 12,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              padding: 12,
              minWidth: 200,
              animation: 'slideUp 0.3s ease'
            }}>
              <button
                onClick={() => handleShare('COMPLETE')}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '10px 12px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderRadius: 8,
                  fontSize: 14
                }}
                onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
                onMouseOut={(e) => e.target.style.background = 'transparent'}
              >
                <strong>Completo</strong>
                <div style={{ fontSize: 12, color: '#666' }}>Mostra todos os nomes</div>
              </button>
              <button
                onClick={() => handleShare('HYBRID')}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '10px 12px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderRadius: 8,
                  fontSize: 14
                }}
                onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
                onMouseOut={(e) => e.target.style.background = 'transparent'}
              >
                <strong>Parcial (Recomendado)</strong>
                <div style={{ fontSize: 12, color: '#666' }}>Mostra apenas iniciais</div>
              </button>
              <button
                onClick={() => handleShare('ANONYMOUS')}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '10px 12px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderRadius: 8,
                  fontSize: 14
                }}
                onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
                onMouseOut={(e) => e.target.style.background = 'transparent'}
              >
                <strong>Anônimo</strong>
                <div style={{ fontSize: 12, color: '#666' }}>Oculta nomes</div>
              </button>
            </div>
          )}
          <button
            onClick={() => setShareMenuOpen(!shareMenuOpen)}
            style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: 'var(--primary-gradient)',
              border: 'none',
              color: 'white',
              fontSize: 24,
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(37, 99, 235, 0.4)',
              transition: 'transform 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'scale(1.1)'}
            onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
          >
            <i className="fa-solid fa-share-nodes"></i>
          </button>
        </div>
      </div>
    );
  }

  // Pagamento pendente - modo manual (sem repasse automático)
  if (payment.autoTransferEnabled === false) {
    return (
      <div className="page-container">
        <div style={{ marginBottom: 32 }}>
          <Link 
            to={`/groups/${payment.groupId}`}
            style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: 8, 
              color: 'var(--text-secondary)',
              marginBottom: 16,
              fontSize: 14
            }}
          >
            <i className="fa-solid fa-arrow-left"></i> Voltar para o grupo
          </Link>
          
          <div className="page-header" style={{ marginBottom: 0 }}>
            <h1 className="page-title">
              <i className="fa-duotone fa-money-bill-transfer page-title-icon"></i>
              Pagamento Manual
            </h1>
          </div>
        </div>

        <div className="alert alert-info animate-fadeIn" style={{ marginBottom: 16 }}>
          <i className="fa-solid fa-circle-info" style={{ marginRight: 6 }}></i>
          <strong>Pagamento direto:</strong> Faça o pagamento diretamente para a chave PIX do recebedor.
        </div>

        <div className="card-modern animate-fadeIn" style={{ padding: 32 }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: 24,
            paddingBottom: 24,
            borderBottom: '1px solid var(--border-color)',
            flexWrap: 'wrap',
            gap: 16
          }}>
            <div>
              <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>Pagando para</p>
              <p style={{ margin: '4px 0 0', fontSize: 18, fontWeight: 600 }}>{payment.receiverName}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>Valor</p>
              <p style={{ 
                margin: '4px 0 0', 
                fontSize: 24, 
                fontWeight: 700,
                color: 'var(--primary-color)'
              }}>
                {formatCurrency(payment.amount)}
              </p>
            </div>
          </div>

          {payment.receiverPixKey ? (
            <>
              {/* Alerta informativo para o recebedor */}
              {isReceiver && !payment.manualPaid && (
                <div className="alert alert-info" style={{ marginBottom: 16 }}>
                  <i className="fa-solid fa-info-circle" style={{ marginRight: 6 }}></i>
                  <strong>Aguardando pagamento:</strong> O pagador deve transferir o valor para sua chave PIX e marcar como pago.
                </div>
              )}

              <label style={{ 
                display: 'block', 
                fontSize: 14, 
                fontWeight: 600, 
                marginBottom: 8,
                color: 'var(--text-secondary)'
              }}>
                Chave PIX do recebedor
              </label>
              <div style={{ 
                display: 'flex', 
                gap: 8,
                alignItems: 'stretch',
                marginBottom: 24
              }}>
                <input 
                  type="text"
                  value={payment.receiverPixKey}
                  readOnly
                  className="form-input"
                  style={{ 
                    flex: 1,
                    fontSize: 14,
                    fontFamily: 'monospace',
                    background: 'var(--bg-secondary)'
                  }}
                />
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText(payment.receiverPixKey);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 3000);
                  }}
                  className={`btn ${copied ? 'btn-success' : 'btn-primary'}`}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <i className={`fa-solid ${copied ? 'fa-check' : 'fa-copy'}`}></i>
                  {copied ? ' Copiado!' : ' Copiar'}
                </button>
              </div>

              <div className="alert alert-warning" style={{ marginBottom: 16 }}>
                <strong>Importante:</strong> Após realizar o pagamento, clique em "Marcar como pago" abaixo para notificar o recebedor.
              </div>

              {/* Botão Marcar como Pago - visível apenas para o pagador */}
              {isPayer && !payment.manualPaid && (
                <button
                  className="btn btn-success"
                  style={{ width: '100%' }}
                  disabled={manualActionLoading}
                  onClick={async () => {
                    try {
                      setManualActionLoading(true);
                      const updated = await paymentService.markManualPaid(payment.id);
                      setPayment(updated);
                      alert('✓ Pagamento marcado como pago! O recebedor será notificado.');
                    } catch (err) {
                      alert(err.response?.data?.message || 'Erro ao marcar como pago');
                    } finally {
                      setManualActionLoading(false);
                    }
                  }}
                >
                  <i className="fa-solid fa-check-circle"></i>
                  {manualActionLoading ? ' Marcando...' : ' Marcar como Pago'}
                </button>
              )}

              {/* Status quando já marcado como pago */}
              {payment.manualPaid && (
                <div className="alert alert-success" style={{ marginTop: 16 }}>
                  <i className="fa-solid fa-check-circle" style={{ marginRight: 6 }}></i>
                  <strong>Pagamento marcado como pago!</strong>
                  {payment.manualPaidAt && (
                    <div style={{ fontSize: 13, marginTop: 4 }}>
                      Em {formatDate(payment.manualPaidAt)}
                    </div>
                  )}
                  {!payment.manualReceived && (
                    <div style={{ fontSize: 13, marginTop: 4 }}>
                      Aguardando confirmação do recebedor.
                    </div>
                  )}
                  {payment.manualReceived && (
                    <div style={{ fontSize: 13, marginTop: 4, color: '#155724' }}>
                      ✓ Recebimento confirmado pelo recebedor em {formatDate(payment.manualReceivedAt)}
                    </div>
                  )}
                </div>
              )}

              {/* Botão Confirmar Recebimento - visível apenas para o recebedor após pagador marcar como pago */}
              {isReceiver && payment.manualPaid && !payment.manualReceived && (
                <button
                  className="btn btn-success"
                  style={{ width: '100%', marginTop: 16 }}
                  disabled={manualActionLoading}
                  onClick={async () => {
                    try {
                      setManualActionLoading(true);
                      const updated = await paymentService.markManualReceived(payment.id);
                      setPayment(updated);
                      alert('✓ Recebimento confirmado com sucesso!');
                    } catch (err) {
                      alert(err.response?.data?.message || 'Erro ao confirmar recebimento');
                    } finally {
                      setManualActionLoading(false);
                    }
                  }}
                >
                  <i className="fa-solid fa-check-double"></i>
                  {manualActionLoading ? ' Confirmando...' : ' Confirmar Recebimento'}
                </button>
              )}
            </>
          ) : (
            <div className="alert alert-warning" style={{ marginBottom: 16 }}>
              <i className="fa-solid fa-circle-exclamation" style={{ marginRight: 6 }}></i>
              O recebedor ainda não cadastrou a chave PIX. Aguarde ou solicite que ele cadastre em
              {' '}
              <Link to="/settings/pix" style={{ fontWeight: 600 }}>Minha chave PIX</Link>.
            </div>
          )}
        </div>

        {/* Botão Flutuante de Compartilhar - Modo Manual */}
        <div style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1000
        }}>
          {shareMenuOpen && (
            <div style={{
              position: 'absolute',
              bottom: 70,
              right: 0,
              background: 'white',
              borderRadius: 12,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              padding: 12,
              minWidth: 200,
              animation: 'slideUp 0.3s ease'
            }}>
              <button
                onClick={() => handleShare('COMPLETE')}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '10px 12px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderRadius: 8,
                  fontSize: 14
                }}
                onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
                onMouseOut={(e) => e.target.style.background = 'transparent'}
              >
                <strong>Completo</strong>
                <div style={{ fontSize: 12, color: '#666' }}>Mostra todos os nomes</div>
              </button>
              <button
                onClick={() => handleShare('HYBRID')}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '10px 12px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderRadius: 8,
                  fontSize: 14
                }}
                onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
                onMouseOut={(e) => e.target.style.background = 'transparent'}
              >
                <strong>Parcial (Recomendado)</strong>
                <div style={{ fontSize: 12, color: '#666' }}>Mostra apenas iniciais</div>
              </button>
              <button
                onClick={() => handleShare('ANONYMOUS')}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '10px 12px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderRadius: 8,
                  fontSize: 14
                }}
                onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
                onMouseOut={(e) => e.target.style.background = 'transparent'}
              >
                <strong>Anônimo</strong>
                <div style={{ fontSize: 12, color: '#666' }}>Oculta nomes</div>
              </button>
            </div>
          )}
          <button
            onClick={() => setShareMenuOpen(!shareMenuOpen)}
            style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: 'var(--primary-gradient)',
              border: 'none',
              color: 'white',
              fontSize: 24,
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(37, 99, 235, 0.4)',
              transition: 'transform 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'scale(1.1)'}
            onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
          >
            <i className="fa-solid fa-share-nodes"></i>
          </button>
        </div>
      </div>
    );
  }

  // Pagamento pendente - mostrar QR Code (modo automático)
  return (
    <div className="page-container">
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <Link 
          to={`/groups/${payment.groupId}`}
          style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: 8, 
            color: 'var(--text-secondary)',
            marginBottom: 16,
            fontSize: 14
          }}
        >
          <i className="fa-solid fa-arrow-left"></i> Voltar para o grupo
        </Link>
        
        <div className="page-header" style={{ marginBottom: 0 }}>
          <h1 className="page-title">
            <i className="fa-duotone fa-qrcode page-title-icon"></i>
            Pagar com PIX
          </h1>
        </div>
      </div>

      {payment.receiverHasPixKey === false && (
        <div className="alert alert-warning animate-fadeIn" style={{ marginBottom: 16 }}>
          <i className="fa-solid fa-circle-exclamation" style={{ marginRight: 6 }}></i>
          O recebedor ainda não cadastrou a chave PIX. O repasse será feito assim que ele salvar a chave em
          {' '}
          <Link to="/settings/pix" style={{ fontWeight: 600 }}>Minha chave PIX</Link>.
        </div>
      )}

      {/* Card de Pagamento */}
      <div className="card-modern animate-fadeIn" style={{ padding: 32 }}>
        {/* Informações */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: 24,
          paddingBottom: 24,
          borderBottom: '1px solid var(--border-color)',
          flexWrap: 'wrap',
          gap: 16
        }}>
          <div>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>Pagando para</p>
            <p style={{ margin: '4px 0 0', fontSize: 18, fontWeight: 600 }}>{payment.receiverName}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>Valor total</p>
            <p style={{ 
              margin: '4px 0 0', 
              fontSize: 24, 
              fontWeight: 700,
              color: 'var(--primary-color)'
            }}>
              {formatCurrency(payment.totalAmount)}
            </p>
            {payment.platformFee > 0 && (
              <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
                (inclui taxa de {formatCurrency(payment.platformFee)})
              </p>
            )}
          </div>
        </div>

        {/* QR Code */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          {payment.pixQrCode ? (
            <div 
              style={{
                display: 'inline-block',
                padding: 16,
                background: 'white',
                borderRadius: 12,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }}
            >
              <img 
                src={payment.pixQrCode} 
                alt="QR Code PIX" 
                style={{ 
                  maxWidth: 250, 
                  width: '100%',
                  display: 'block'
                }}
              />
            </div>
          ) : (
            <div style={{ padding: 40, color: 'var(--text-muted)' }}>
              <i className="fa-duotone fa-qrcode" style={{ fontSize: 80, opacity: 0.3 }}></i>
              <p>QR Code não disponível</p>
            </div>
          )}
        </div>

        {/* Código Copia e Cola */}
        {payment.pixCopyPaste && (
          <div style={{ marginBottom: 24 }}>
            <label style={{ 
              display: 'block', 
              fontSize: 14, 
              fontWeight: 600, 
              marginBottom: 8,
              color: 'var(--text-secondary)'
            }}>
              Código PIX (Copia e Cola)
            </label>
            <div style={{ 
              display: 'flex', 
              gap: 8,
              alignItems: 'stretch'
            }}>
              <input 
                type="text"
                value={payment.pixCopyPaste}
                readOnly
                className="form-input"
                style={{ 
                  flex: 1,
                  fontSize: 12,
                  fontFamily: 'monospace',
                  background: 'var(--bg-secondary)'
                }}
              />
              <button 
                onClick={handleCopyPixCode}
                className={`btn ${copied ? 'btn-success' : 'btn-primary'}`}
                style={{ whiteSpace: 'nowrap' }}
              >
                <i className={`fa-solid ${copied ? 'fa-check' : 'fa-copy'}`}></i>
                {copied ? ' Copiado!' : ' Copiar'}
              </button>
            </div>
          </div>
        )}

        {/* Expiração com Contagem Regressiva */}
        <div style={{ 
          background: timeRemaining && timeRemaining.totalSeconds <= 300 
            ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.15))'
            : 'linear-gradient(135deg, rgba(255, 193, 7, 0.1), rgba(255, 152, 0, 0.1))',
          borderRadius: 12,
          padding: 16,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          border: timeRemaining && timeRemaining.totalSeconds <= 300 
            ? '1px solid rgba(239, 68, 68, 0.3)' 
            : 'none',
          transition: 'all 0.3s ease'
        }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: timeRemaining && timeRemaining.totalSeconds <= 300 
              ? 'linear-gradient(135deg, #EF4444, #DC2626)'
              : 'linear-gradient(135deg, #F59E0B, #D97706)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <i className="fa-solid fa-clock" style={{ 
              fontSize: 20, 
              color: 'white',
              animation: timeRemaining && timeRemaining.totalSeconds <= 60 ? 'pulse 1s infinite' : 'none'
            }}></i>
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ 
              margin: 0, 
              fontSize: 14, 
              fontWeight: 600,
              color: timeRemaining && timeRemaining.totalSeconds <= 300 ? '#DC2626' : 'inherit'
            }}>
              {timeRemaining && timeRemaining.totalSeconds <= 300 
                ? 'PIX expirando em breve!' 
                : 'Este PIX expira em'}
            </p>
            <p style={{ 
              margin: '4px 0 0', 
              fontSize: 28, 
              fontWeight: 700,
              fontFamily: 'monospace',
              color: timeRemaining && timeRemaining.totalSeconds <= 300 ? '#DC2626' : 'var(--primary-color)',
              letterSpacing: '2px'
            }}>
              {formatTimeRemaining(timeRemaining)}
            </p>
          </div>
          {timeRemaining && timeRemaining.totalSeconds <= 300 && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              padding: '8px 12px',
              borderRadius: 8,
              fontSize: 12,
              color: '#DC2626',
              fontWeight: 500
            }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 4 }}></i>
              Pague agora!
            </div>
          )}
        </div>

        {/* Status e Ações */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap'
        }}>
          <button 
            onClick={refreshPaymentStatus}
            className="btn btn-secondary"
            disabled={refreshing}
          >
            <i className={`fa-solid fa-rotate ${refreshing ? 'fa-spin' : ''}`}></i>
            {refreshing ? ' Verificando...' : ' Verificar Pagamento'}
          </button>
          
          <button 
            onClick={handleCancel}
            className="btn btn-danger"
          >
            <i className="fa-solid fa-xmark"></i> Cancelar
          </button>
        </div>
      </div>

      {/* Instruções */}
      <div className="card-modern animate-fadeIn" style={{ padding: 24, marginTop: 24, animationDelay: '0.1s' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>
          <i className="fa-duotone fa-lightbulb" style={{ marginRight: 8, color: '#F59E0B' }}></i>
          Como pagar
        </h3>
        <ol style={{ margin: 0, paddingLeft: 20, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
          <li>Abra o app do seu banco</li>
          <li>Acesse a área PIX e selecione "Pagar com QR Code" ou "Copia e Cola"</li>
          <li>Escaneie o QR Code acima ou cole o código PIX</li>
          <li>Confirme o pagamento</li>
          <li>Volte aqui e clique em "Verificar Pagamento"</li>
        </ol>
      </div>

      {/* Botão Flutuante de Compartilhar */}
      <div style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 1000
      }}>
        {shareMenuOpen && (
          <div style={{
            position: 'absolute',
            bottom: 70,
            right: 0,
            background: 'white',
            borderRadius: 12,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            padding: 12,
            minWidth: 200,
            animation: 'slideUp 0.3s ease'
          }}>
            <button
              onClick={() => handleShare('COMPLETE')}
              style={{
                display: 'block',
                width: '100%',
                padding: '10px 12px',
                border: 'none',
                background: 'transparent',
                textAlign: 'left',
                cursor: 'pointer',
                borderRadius: 8,
                fontSize: 14
              }}
              onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
              onMouseOut={(e) => e.target.style.background = 'transparent'}
            >
              <strong>Completo</strong>
              <div style={{ fontSize: 12, color: '#666' }}>Mostra todos os nomes</div>
            </button>
            <button
              onClick={() => handleShare('HYBRID')}
              style={{
                display: 'block',
                width: '100%',
                padding: '10px 12px',
                border: 'none',
                background: 'transparent',
                textAlign: 'left',
                cursor: 'pointer',
                borderRadius: 8,
                fontSize: 14
              }}
              onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
              onMouseOut={(e) => e.target.style.background = 'transparent'}
            >
              <strong>Parcial (Recomendado)</strong>
              <div style={{ fontSize: 12, color: '#666' }}>Mostra apenas iniciais</div>
            </button>
            <button
              onClick={() => handleShare('ANONYMOUS')}
              style={{
                display: 'block',
                width: '100%',
                padding: '10px 12px',
                border: 'none',
                background: 'transparent',
                textAlign: 'left',
                cursor: 'pointer',
                borderRadius: 8,
                fontSize: 14
              }}
              onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
              onMouseOut={(e) => e.target.style.background = 'transparent'}
            >
              <strong>Anônimo</strong>
              <div style={{ fontSize: 12, color: '#666' }}>Oculta nomes</div>
            </button>
          </div>
        )}
        <button
          onClick={() => setShareMenuOpen(!shareMenuOpen)}
          style={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: 'var(--primary-gradient)',
            border: 'none',
            color: 'white',
            fontSize: 24,
            cursor: 'pointer',
            boxShadow: '0 4px 15px rgba(37, 99, 235, 0.4)',
            transition: 'transform 0.3s ease'
          }}
          onMouseOver={(e) => e.target.style.transform = 'scale(1.1)'}
          onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
        >
          <i className="fa-solid fa-share-nodes"></i>
        </button>
      </div>
    </div>
  );
};

export default PaymentPage;
